import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { BACKEND_URL } from '../utils/config';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Users,
  Heart,
  ArrowLeft,
  Loader2,
  ImagePlus,
  Target,
  Info,
  CheckCircle2,
} from 'lucide-react';

const CreateTeamPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    story: '',
    goal_amount: '',
    cover_image_url: '',
  });

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/campaigns/${campaignId}`);
        if (!response.ok) throw new Error('Campaign not found');
        const data = await response.json();
        
        if (!data.team_fundraising_enabled) {
          setError('Team fundraising is not enabled for this campaign');
        }
        setCampaign(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCampaign();
  }, [campaignId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate(`/auth?tab=signin&redirect=/campaign/${campaignId}/fundraise`);
      return;
    }
    
    if (!formData.title || formData.title.length < 5) {
      setError('Title must be at least 5 characters');
      return;
    }
    
    if (!formData.goal_amount || parseFloat(formData.goal_amount) < 50) {
      setError('Goal amount must be at least $50');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/team-pages/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          parent_campaign_id: campaignId,
          title: formData.title,
          story: formData.story,
          goal_amount: parseFloat(formData.goal_amount),
          milestone_steps: null, // Will auto-generate default milestones
          cover_image_url: formData.cover_image_url || null,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create team page');
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate(`/fundraise/${data.slug}`);
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!campaign || error === 'Campaign not found') {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground mb-2">Campaign Not Found</h2>
            <Link to="/discover">
              <Button className="bg-emerald-500 hover:bg-emerald-600">
                Discover Campaigns
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!campaign.team_fundraising_enabled) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1">
          <div className="max-w-2xl mx-auto px-4 py-12 text-center">
            <div className="w-16 h-16 bg-gray-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Team Fundraising Not Available</h2>
            <p className="text-muted-foreground mb-6">
              The creator of this campaign hasn't enabled team fundraising yet.
            </p>
            <Link to={`/campaign/${campaignId}`}>
              <Button className="bg-emerald-500 hover:bg-emerald-600">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Campaign
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Team Page Created!</h2>
            <p className="text-muted-foreground">Redirecting to your fundraising page...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1">
        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Back Link */}
          <Link to={`/campaign/${campaignId}`} className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to campaign
          </Link>

          {/* Header */}
          <div className="text-center mb-8">
            <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 mb-4">
              <Users className="w-3 h-3 mr-1" /> Team Fundraising
            </Badge>
            <h1 className="text-3xl font-bold text-foreground mb-2">Start Your Fundraising Page</h1>
            <p className="text-muted-foreground">
              Create a personal fundraising page to support "{campaign.title}"
            </p>
          </div>

          {/* Campaign Preview Card */}
          <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-4 mb-8">
            <div className="flex items-center gap-4">
              <img
                src={campaign.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(campaign.title || 'Campaign')}&background=10b981&color=fff&size=128`}
                alt={campaign.title}
                className="w-16 h-16 rounded-lg object-cover bg-secondary"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(campaign.title || 'Campaign')}&background=10b981&color=fff&size=128`;
                }}
              />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">You're fundraising for</p>
                <h3 className="font-semibold text-foreground">{campaign.title}</h3>
              </div>
              <Target className="w-6 h-6 text-emerald-500" />
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-500 text-sm">
                {error}
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Page Title *
              </label>
              <Input
                placeholder="e.g., Sarah's Birthday Fundraiser"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full"
                required
                minLength={5}
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Make it personal and engaging (5-100 characters)
              </p>
            </div>

            {/* Story */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Why I'm Fundraising
              </label>
              <Textarea
                placeholder="Share why this cause is important to you and why others should support..."
                value={formData.story}
                onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                rows={4}
                maxLength={5000}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tell your personal connection to this cause
              </p>
            </div>

            {/* Goal Amount */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Fundraising Goal *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  placeholder="500"
                  value={formData.goal_amount}
                  onChange={(e) => setFormData({ ...formData, goal_amount: e.target.value })}
                  className="pl-8"
                  min={50}
                  max={10000000}
                  required
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Set your fundraising goal (minimum $50). Milestones at 25%, 50%, 75%, and 100% will be auto-generated.
              </p>
            </div>

            {/* Cover Image */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Cover Image URL (Optional)
              </label>
              <div className="flex gap-3">
                <Input
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={formData.cover_image_url}
                  onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })}
                  className="flex-1"
                />
                <Button type="button" variant="outline" disabled>
                  <ImagePlus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Add a personal image or use the campaign's default image
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-secondary/50 rounded-xl p-4 border border-border">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">How Team Fundraising Works</p>
                  <ul className="space-y-1">
                    <li>• All donations go directly to the main campaign</li>
                    <li>• Your page tracks donations made through your link</li>
                    <li>• Share your unique page with friends and family</li>
                    <li>• See your impact on the campaign leaderboard</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting || !formData.title}
              className="w-full py-6 text-lg bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creating...
                </>
              ) : (
                <>
                  <Heart className="w-5 h-5 mr-2" /> Create My Fundraising Page
                </>
              )}
            </Button>
          </form>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CreateTeamPage;
